import React, { useRef, useEffect, useState } from 'react';
import { InfoIcon, XCircleIcon } from '../../lib/icons';

export const Card = ({ children, className }) => <div className={`bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl shadow-lg ${className}`}>{children}</div>;

export const CardHeader = ({ icon, title, subtitle }) => (
    <div className="p-4 border-b border-gray-700/50 flex items-center space-x-3">
        <div className="text-blue-400 flex-shrink-0 w-8 h-8 flex items-center justify-center">{icon}</div>
        <div>
            <h3 className="font-bold text-white">{title}</h3>
            {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
        </div>
    </div>
);

const Tooltip = ({ content, isVisible, onClose }) => {
    if (!isVisible) return null;
    return (
        <div className="absolute bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-md shadow-lg z-20 border border-gray-600">
            <button onClick={onClose} className="absolute top-1 right-1 text-gray-400 hover:text-white">
                <XCircleIcon width={16} height={16} />
            </button>
            {content}
        </div>
    );
};

export const Input = ({ id, label, type = 'number', value, onChange, unit, tooltip, activeTooltip, setActiveTooltip }) => (
    <div>
        <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center">
            {label}
            {tooltip && (
                <div className="relative ml-2">
                    <button onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === id ? null : id); }}>
                        <InfoIcon />
                    </button>
                    <Tooltip content={tooltip} isVisible={activeTooltip === id} onClose={() => setActiveTooltip(null)} />
                </div>
            )}
        </label>
        <div className="relative">
            <input type={type} value={value} onChange={onChange} className="w-full bg-gray-900/50 border border-gray-600 rounded-md p-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"/>
            {unit && <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-400">{unit}</span>}
        </div>
    </div>
);

export const Select = ({ id, label, value, onChange, options, tooltip, activeTooltip, setActiveTooltip, displaySymbol = false }) => (
    <div>
        <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center">
            {label}
             {tooltip && (
                <div className="relative ml-2">
                    <button onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === id ? null : id); }}>
                        <InfoIcon />
                    </button>
                    <Tooltip content={tooltip} isVisible={activeTooltip === id} onClose={() => setActiveTooltip(null)} />
                </div>
            )}
        </label>
        <select value={value} onChange={onChange} className="w-full bg-gray-900/50 border border-gray-600 rounded-md p-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition">
            {Object.entries(options).map(([key, val]) => (
                <option key={key} value={key}>{displaySymbol ? `${val.symbol} (${val.name})` : (val.name || val)}</option>
            ))}
        </select>
    </div>
);

export const Button = ({ children, onClick, className = '', disabled=false }) => (
    <button onClick={onClick} disabled={disabled} className={`w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-md hover:bg-blue-700 transition-all transform hover:scale-105 disabled:bg-gray-500 disabled:cursor-not-allowed disabled:scale-100 ${className}`}>
        {children}
    </button>
);

export const LineChart = ({ data, xAxisKey, yAxisKeys, auctionType, correctedCost, currency }) => {
  const containerRef = useRef();
  const [dims, setDims] = useState({ width: 800, height: 350 });

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        const { width } = entries[0].contentRect;
        setDims({ width: width, height: Math.max(350, width * 0.4) });
      }
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  const { width, height } = dims;
  const margin = { top: 20, right: 50, bottom: 50, left: 60 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  if (!data || data.length === 0) {
    return <div ref={containerRef} className="flex items-center justify-center h-[350px] bg-gray-800/80 rounded-lg"><p>Run simulation to see chart.</p></div>;
  }
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currency.symbol}0M`;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.name,
        maximumFractionDigits: 1,
        notation: 'compact'
    }).format(value);
  }

  const xValues = data.map(d => d[xAxisKey]);
  const yValuesProfit = data.map(d => d.expectedProfit);
  
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMinProb = 0;
  const yMaxProb = 1;
  const yMinProfit = Math.min(0, ...yValuesProfit);
  const yMaxProfit = Math.max(...yValuesProfit);

  const xScale = (x) => (x - xMin) / (xMax - xMin) * chartWidth;
  const yScaleProb = (y) => chartHeight - ((y - yMinProb) / (yMaxProb - yMinProb)) * chartHeight;
  const yScaleProfit = (y) => chartHeight - ((y - yMinProfit) / (yMaxProfit - yMinProfit)) * chartHeight;

  const optimalBidPoint = (auctionType === 'FIRST_PRICE' || auctionType === 'DUTCH') ? data.reduce((max, p) => p.expectedProfit > max.expectedProfit ? p : max, data[0]) : null;
  const biddingLimitX = (auctionType === 'ENGLISH' || auctionType === 'VICKREY') ? xScale(correctedCost) : null;

  return (
    <div ref={containerRef} className="bg-gray-800 p-4 rounded-lg shadow-inner">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          <line x1="0" y1={chartHeight} x2={chartWidth} y2={chartHeight} stroke="#4a5568" />
          {[0, 0.25, 0.5, 0.75, 1].map(tick => (
             <g key={tick} transform={`translate(${xScale(xMin + tick * (xMax - xMin))}, ${chartHeight})`}>
                <text y="20" textAnchor="middle" fill="#a0aec0" fontSize="10">{formatCurrency(xMin + tick * (xMax - xMin))}</text>
             </g>
          ))}
          <text x={chartWidth / 2} y={height - 10} textAnchor="middle" fill="#a0aec0" fontSize="12">Your Bid Price</text>
          <line x1="0" y1="0" x2="0" y2={chartHeight} stroke={yAxisKeys[0].color} />
          {[0, 0.25, 0.5, 0.75, 1].map(tick => (
             <g key={tick}>
                 <text x="-10" y={yScaleProb(tick) + 4} textAnchor="end" fill={yAxisKeys[0].color} fontSize="10">{tick.toFixed(2)}</text>
                 <line x1="-5" y1={yScaleProb(tick)} x2={chartWidth} y2={yScaleProb(tick)} stroke="#374151" strokeDasharray="2,2"/>
             </g>
          ))}
          <text transform={`rotate(-90)`} x={-chartHeight / 2} y={-45} textAnchor="middle" fill={yAxisKeys[0].color} fontSize="12">Win Probability</text>
          <line x1={chartWidth} y1="0" x2={chartWidth} y2={chartHeight} stroke={yAxisKeys[1].color} />
           {[0, 0.25, 0.5, 0.75, 1].map(tick => (
             <g key={tick}>
                 <text x={chartWidth + 8} y={yScaleProfit(yMinProfit + tick * (yMaxProfit - yMinProfit)) + 4} textAnchor="start" fill={yAxisKeys[1].color} fontSize="10">{formatCurrency(yMinProfit + tick * (yMaxProfit - yMinProfit))}</text>
             </g>
          ))}
          <text transform={`rotate(90)`} x={chartHeight/2} y={-width+25} textAnchor="middle" fill={yAxisKeys[1].color} fontSize="12">Expected Profit</text>
          <polyline fill="none" stroke={yAxisKeys[0].color} strokeWidth="2" points={data.map(d => `${xScale(d.bid)},${yScaleProb(d.winProbability)}`).join(' ')} />
          <polyline fill="none" stroke={yAxisKeys[1].color} strokeWidth="2" points={data.map(d => `${xScale(d.bid)},${yScaleProfit(d.expectedProfit)}`).join(' ')} />
          {optimalBidPoint && (
            <g>
              <circle cx={xScale(optimalBidPoint.bid)} cy={yScaleProfit(optimalBidPoint.expectedProfit)} r="5" fill={yAxisKeys[1].color} stroke="white" strokeWidth="2" />
              <text x={xScale(optimalBidPoint.bid)} y={yScaleProfit(optimalBidPoint.expectedProfit) - 10} textAnchor="middle" fill="white" fontSize="10" className="font-bold">Optimal Bid</text>
            </g>
          )}
          {biddingLimitX !== null && biddingLimitX >= 0 && biddingLimitX <= chartWidth && (
            <g>
              <line x1={biddingLimitX} y1="0" x2={biddingLimitX} y2={chartHeight} stroke="white" strokeWidth="2" strokeDasharray="4,4" />
              <text x={biddingLimitX + 5} y="15" fill="white" fontSize="10">Bidding Limit</text>
            </g>
          )}
        </g>
      </svg>
    </div>
  );
};
